import React, { useCallback, useEffect, useState } from 'react';
import { format, addMonths, subMonths, subDays, endOfMonth, startOfMonth, differenceInDays } from "date-fns";
import { ChevronLeft, ChevronRight, Dice5 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { getDateString, getNormalizedDate } from '@/utils/dateUtils';
import { useGame } from '@/context/GameContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSwipe } from '@/hooks/use-swipe';
import { GameMode, GameStatus } from '@/utils/gameState';

interface ArchiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDateSelect: (date: Date) => void;
  onRandomSelect: () => void;
}

const START_DATE = getNormalizedDate(new Date(2021, 5, 19)); // June 19, 2021 (Fourdle start date)

const ArchiveModal: React.FC<ArchiveModalProps> = ({ isOpen, onClose, onDateSelect, onRandomSelect }) => {
  const yesterday = subDays(getNormalizedDate(), 1);
  const { gameConfig, gameCompletions } = useGame();
  const [date, setDate] = useState<Date>(gameConfig.date ?? getNormalizedDate()); // If no date is selected, use today's date
  const [lastDate, _setLastDate] = useState<Date>(yesterday);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isOpen) return;

    const anchorDate = gameConfig.mode === GameMode.ARCHIVE && gameConfig.date
      ? getNormalizedDate(gameConfig.date)
      : getNormalizedDate();

    setDate(anchorDate);
  }, [isOpen, gameConfig.mode, gameConfig.date]);
  
  const generateDateOptions = useCallback(() => {
    // Generate date options for the combined dropdown
    const dateOptions = [];
    
    // Generate options from start date to lastDate
    let tempDate = new Date(START_DATE);
    while (tempDate <= lastDate) {
      dateOptions.push({
        value: `${tempDate.getFullYear()}-${tempDate.getMonth() + 1}`,
        label: format(tempDate, "MMMM yyyy")
      });

      // Move to next month
      tempDate = startOfMonth(addMonths(tempDate, 1));
    }
  
    // Reverse the array to show most recent months first
    dateOptions.reverse();

    return dateOptions;
  }, [lastDate]);

  const getDefaultMonthYear = useCallback((): string => {
    return `${date.getFullYear()}-${date.getMonth() + 1}`;
  }, [date]);

  const handleDateSelect = useCallback((newDate: Date | undefined) => {
    if (newDate) {
      onDateSelect(getNormalizedDate(newDate));
      onClose();
    }
  }, [onDateSelect, onClose]);

  const handleMonthYearChange = useCallback((monthYearValue: string) => {
    const [year, month] = monthYearValue.split('-').map(Number);
    // Create a new date with the selected month and year, but keep the current day
    // month is 1-indexed (1-12), so subtract 1 for Date constructor
    const newDate = new Date(year, month - 1, date.getDate());
    
    // Adjust the day if it would be invalid in the new month
    // (e.g., trying to set February 30)
    const maxDaysInMonth = new Date(year, month + 1, 0).getDate();
    if (date.getDate() > maxDaysInMonth) {
      newDate.setDate(maxDaysInMonth);
    }
    
    setDate(newDate);
  }, [date]);

  // Handle month navigation via calendar buttons
  const handleMonthChange = useCallback((newMonth: Date) => {
    setDate(newMonth);
  }, []);

  // Handle previous month
  const goToPreviousMonth = useCallback(() => {
    const newDate = endOfMonth(subMonths(date, 1));
    if (newDate >= START_DATE) {
      setDate(newDate);
    }
  }, [date]);

  // Handle next month
  const goToNextMonth = useCallback(() => {
    const newDate = startOfMonth(addMonths(date, 1));
    if (newDate <= lastDate) {
      setDate(newDate);
    }
  }, [date, lastDate]);

  // Set up swipe handlers
  const swipeHandlers = useSwipe({
    onSwipeLeft: goToNextMonth,
    onSwipeRight: goToPreviousMonth,
    threshold: 50
  });

  // Handle random date selection
  const handleRandomDateSelect = useCallback(() => {
    onRandomSelect();
    onClose();
  }, [onRandomSelect, onClose]);

  const dayModifiers = {
    day_won: (day: Date) => {
      const dayStr = getDateString(getNormalizedDate(day));
      return gameCompletions[dayStr] === GameStatus.WIN;
    },
    day_lost: (day: Date) => {
      const dayStr = getDateString(getNormalizedDate(day));
      return gameCompletions[dayStr] === GameStatus.LOSE;
    },
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="w-auto">
        <DialogHeader>
          <DialogTitle className="text-center">Archive</DialogTitle>
        </DialogHeader>
        
        <div className="flex gap-2">
          <Select 
            value={`${date.getFullYear()}-${date.getMonth() + 1}`}
            onValueChange={handleMonthYearChange}
            defaultValue={getDefaultMonthYear()}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select month and year" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {generateDateOptions().map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            onClick={handleRandomDateSelect} 
            variant="outline" 
            className="flex gap-2 items-center" 
            title="Select random unplayed game"
          >
            <Dice5 className="h-4 w-4" />
            <span>Random</span>
          </Button>
        </div>

        <div 
          {...(isMobile ? swipeHandlers : {})}
          className="touch-pan-y"
        >
          <Calendar
            mode="single"
            selected={gameConfig.date}
            onSelect={handleDateSelect}
            className="rounded-md border pointer-events-auto" 
            fromDate={START_DATE}
            toDate={lastDate}
            captionLayout="buttons"
            month={date}
            defaultMonth={date}
            onMonthChange={handleMonthChange}
            modifiers={dayModifiers}
            modifiersClassNames={{
              day_won: 'bg-green-200 dark:bg-green-800 rounded-sm',
              day_lost: 'bg-red-200 dark:bg-red-900 rounded-sm',
            }}
            classNames={{
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              head_cell: "w-9 font-normal text-muted-foreground",
              cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
              day: "h-9 w-9 p-0 font-normal",
              day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-sm",
              day_today: "bg-accent text-accent-foreground rounded-sm",
            }}
            components={{
              IconLeft: () => <ChevronLeft className="h-4 w-4" />,
              IconRight: () => <ChevronRight className="h-4 w-4" />,
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ArchiveModal;
